import React, { FC, useState } from 'react';
import { MutationFunction } from 'react-apollo';
import { Formik, Form as FormikForm } from 'formik';
import { Grid } from '@material-ui/core';
import { find } from 'lodash';
import moment from 'moment';

import {
  Group,
  RunReportInput,
  ReportFilterType,
  ReportRequestList,
  GetReportRequestsComponent,
  CreateReportRequestComponent,
  CreateReportRequestMutation,
} from 'api';
import { useNotify } from 'hooks';
import { PaddedPaper, QueryManager } from 'components';
import { ReportsSchema } from 'features/admin/validations';

import Heading from './Heading';
import ReportDates from './ReportDates';
import Actions from './Actions';

interface IProps {
  group: Group;
}

const REPORT_NAME = 'Content';
const REPORT_INTERVAL = 1000;
const REPORT_LIMIT = 3;

const START_AT = moment()
  .subtract(8, 'd')
  .startOf('d')
  .unix();
const END_AT = moment()
  .subtract(1, 'd')
  .startOf('d')
  .unix();

const ReportIndex: FC<IProps> = ({ group }) => {
  const [pollInterval, setPollInterval] = useState<number>();
  const [requestId, setRequestId] = useState<string>();
  const notify = useNotify();

  function handleSubmit(mutation: MutationFunction, data: RunReportInput) {
    setPollInterval(REPORT_INTERVAL);
    return mutation({ variables: { data } });
  }

  function handleCompleted(data: CreateReportRequestMutation) {
    const { id } = data.createReportRequest;
    setRequestId(id);
  }

  function handleDownload() {
    setPollInterval(0);
  }

  function renderMutation(reportRequests: ReportRequestList) {
    const initialValues: RunReportInput = {
      report_name: REPORT_NAME,
      start_at: START_AT,
      end_at: END_AT,
      filter_by: [{ filter_type: ReportFilterType.Group, filter_ids: [group.id] }],
    };

    const { report_requests } = reportRequests;
    const request = find(report_requests, ['id', requestId]);

    return (
      <Grid item>
        <PaddedPaper>
          <CreateReportRequestComponent
            onError={notify.mutationError}
            onCompleted={handleCompleted}>
            {mutationFn => (
              <Formik
                isInitialValid
                initialValues={initialValues}
                onSubmit={values => handleSubmit(mutationFn, values)}
                validationSchema={ReportsSchema}
                render={() => (
                  <FormikForm>
                    <Heading />
                    <ReportDates />
                    <Actions
                      reportUrl={request && request.report_url}
                      working={!!pollInterval}
                      onDownload={handleDownload}
                    />
                  </FormikForm>
                )}
              />
            )}
          </CreateReportRequestComponent>
        </PaddedPaper>
      </Grid>
    );
  }

  return (
    <GetReportRequestsComponent
      fetchPolicy="network-only"
      variables={{ data: { limit: REPORT_LIMIT } }}
      pollInterval={pollInterval}>
      {queryResult => (
        <QueryManager loader="none" {...queryResult}>
          {({ data }) => {
            if (!data || !data.getReportRequests) {
              return null;
            }

            return renderMutation(data.getReportRequests);
          }}
        </QueryManager>
      )}
    </GetReportRequestsComponent>
  );
};

export default ReportIndex;
